"use client";

import { useState } from "react";

import moment from "moment";
import { useTranslations } from "next-intl";

import { Typography } from "@/shared/ui";

// import { TimelineGauge } from "./TimelineGauge";
import { TimelineGauge } from "./TimelineGaugeV2";
import { WakeTimeSelect } from "./WakeTimeSelect";

type WakeScheduleProps = {
    age: string;
    ageText: string;
};

const formatTime = (time: moment.Moment) => {
    return time.format("HH:mm");
};

const napParams = {
    "0-3 months": null,
    "3-4 months": {
        napsDurations: [60, 60, 60, 60],
        wakeDurations: [75, 90, 105, 105, 120],
    },
    "5-6 months": {
        napsDurations: [90, 90, 30],
        wakeDurations: [2 * 60, 2 * 60, 2 * 60, 2.5 * 60],
    },
    "7-12 months": {
        napsDurations: [90, 90],
        wakeDurations: [2.5 * 60, 3.5 * 60, 3 * 60],
    },
    "12-18 months": {
        napsDurations: [90, 90],
        wakeDurations: [2.5 * 60, 3 * 60, 3.5 * 60],
    },
    "18-24 months": {
        napsDurations: [120],
        wakeDurations: [5 * 60, 5 * 60],
    },
    // eslint-disable-next-line perfectionist/sort-objects
    "2 years": {
        napsDurations: [120],
        wakeDurations: [5 * 60, 5 * 60],
    },
    // eslint-disable-next-line perfectionist/sort-objects
    "3 years": {
        napsDurations: [60],
        wakeDurations: [5.5 * 60, 5.5 * 60],
    },
    "4 years": {
        napsDurations: [],
        wakeDurations: [12 * 60],
    },
    "5 years": {
        napsDurations: [],
        wakeDurations: [12 * 60],
    },
};

export const WakeSchedule = ({ age, ageText }: WakeScheduleProps) => {
    const t = useTranslations("WakeSchedule");
    const [selectedHour, setSelectedHour] = useState(7);
    const [selectedMinute, setSelectedMinute] = useState(0);
    const napData = (napParams as any)[age];

    const title = (
        <>
            <Typography className="text-black mb-4" variant="headline">
                {t("proposedSchedule")} {ageText}
            </Typography>
        </>
    );

    if (!napData) {
        return null;
    }
    const { justText, napsDurations, wakeDurations } = napData;
    if (justText) {
        return (
            <>
                {title}
                <Typography className="text-black mt-4" variant="body">
                    {justText(t)}
                </Typography>
            </>
        );
    }
    const wakeTime = moment().hour(selectedHour).minute(selectedMinute).second(0);
    const handleTimeChange = (hour: number, minute: number) => {
        setSelectedHour(hour);
        setSelectedMinute(minute);
    };
    let currentTime = moment(wakeTime);

    // Calculate all nap times
    const schedule = napsDurations.map((napDuration: number, index: number) => {
        const napStart = moment(currentTime).add(wakeDurations[index], "minutes");
        currentTime = moment(napStart);
        const napEnd = moment(currentTime).add(napDuration, "minutes");
        currentTime = moment(napEnd);

        return {
            end: napEnd,
            napNumber: index + 1,
            start: napStart,
        };
    });

    // Calculate bedtime using the last wake duration
    const bedTime = moment(currentTime).add(wakeDurations[wakeDurations.length - 1], "minutes");

    return (
        <>
            <WakeTimeSelect onTimeChange={handleTimeChange} />
            {title}
            <div className="mt-2 p-4 bg-white">
                <div className="text-black space-y-2 text-xl max-w-lg">
                    <table className="w-full max-w-[350px] border-separate border-spacing-x-4">
                        <tbody>
                            <tr>
                                <td className="font-medium">{t("wakeUpTime")}</td>
                                <td className="text-left">{formatTime(wakeTime)}</td>
                            </tr>
                            {schedule.map((nap: any, index: number) => (
                                <tr key={index}>
                                    <td className="font-medium">
                                        {t("nap")} {nap.napNumber}
                                    </td>
                                    <td className="text-left">
                                        {formatTime(nap.start)} - {formatTime(nap.end)}
                                    </td>
                                </tr>
                            ))}
                            <tr>
                                <td className="font-medium">{t("bedtime")}</td>
                                <td className="text-left">{formatTime(bedTime)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="">
                <TimelineGauge bedTime={bedTime} naps={schedule} wakeTime={wakeTime} />
            </div>
        </>
    );
};
